// A lane as geometry: straight runs joined by quarter-circle arcs. Pure, so it can be tested without WebGL.
import { PI } from '../lib/math.js';

// A car's lane: the loop's legs offset 0.7 to the right (driving on the right), with every corner rounded into a
// quarter circle that stays on the asphalt: a tight radius turning right (inside lane), a wider one turning left.
export function laneGeometry(pts) {
  const P = pts,
    n = P.length,
    D = [],
    Q = [],
    R = [];
  for (let k = 0; k < n; k++) {
    const [ax, az] = P[k],
      [bx, bz] = P[(k + 1) % n],
      L = Math.hypot(bx - ax, bz - az);
    D.push([(bx - ax) / L, (bz - az) / L]);
  }
  for (let k = 0; k < n; k++) {
    const d0 = D[(k - 1 + n) % n],
      d1 = D[k],
      o0 = [-d0[1] * 0.7, d0[0] * 0.7],
      o1 = [-d1[1] * 0.7, d1[0] * 0.7];
    Q.push([P[k][0] + o0[0] + o1[0], P[k][1] + o0[1] + o1[1]]); // corner k of the offset lane
    const cross = d0[0] * d1[1] - d0[1] * d1[0];
    R.push(cross > 0 ? 2.8 : 4.2);
  }
  const segs = [];
  let total = 0;
  for (let k = 0; k < n; k++) {
    const d = D[k],
      k1 = (k + 1) % n,
      d1 = D[k1],
      r0 = R[k],
      r1 = R[k1];
    const ax = Q[k][0] + d[0] * r0,
      az = Q[k][1] + d[1] * r0,
      bx = Q[k1][0] - d[0] * r1,
      bz = Q[k1][1] - d[1] * r1;
    const len = Math.max(0.01, Math.hypot(bx - ax, bz - az));
    segs.push({ ax, az, dx: d[0], dz: d[1], len, leg: k });
    segs.push({
      arc: true,
      r: r1,
      len: (r1 * PI) / 2,
      cx: bx + d1[0] * r1,
      cz: bz + d1[1] * r1,
      d0x: d[0],
      d0z: d[1],
      d1x: d1[0],
      d1z: d1[1],
      leg: k1,
    });
    total += len + (r1 * PI) / 2;
  }
  return { segs, total };
}

// Point and heading (the path's tangent) at distance s along the lane; `leg` is the loop leg being driven.
export function lanePose(segs, s) {
  let i = 0;
  while (s > segs[i].len) {
    s -= segs[i].len;
    i++;
  }
  const g = segs[i];
  if (g.arc) {
    const th = s / g.r,
      cs = Math.cos(th),
      sn = Math.sin(th);
    return {
      x: g.cx - g.d1x * g.r * cs + g.d0x * g.r * sn,
      z: g.cz - g.d1z * g.r * cs + g.d0z * g.r * sn,
      tx: g.d1x * sn + g.d0x * cs,
      tz: g.d1z * sn + g.d0z * cs,
      leg: g.leg,
    };
  }
  return { x: g.ax + g.dx * s, z: g.az + g.dz * s, tx: g.dx, tz: g.dz, leg: g.leg };
}

// Is the point (x, z) inside the rectangle of a vehicle at (cx, cz) facing `heading`, grown by `pad` on every side?
export function pointInFootprint(x, z, cx, cz, heading, len, w, pad = 0) {
  const fx = Math.sin(heading),
    fz = Math.cos(heading),
    dx = x - cx,
    dz = z - cz;
  return Math.abs(dx * fx + dz * fz) < len / 2 + pad && Math.abs(dx * fz - dz * fx) < w / 2 + pad;
}
